import { assetsPresentMap, requestStatusMap } from './common';
import type { AssetLayer, RequestableLayer } from './store';

interface Config {
  property: string;
  heading: string;
  getValue: (asset: RequestableLayer | AssetLayer) => string;
}

const isValidStatus = (asset: RequestableLayer): asset is RequestableLayer => {
  if (asset.status && asset.status !== 'request' && asset.status !== 'dismissed') {
    return true;
  }

  return false;
};

export const config: { [key: string]: Config } = {
  sourceDate: {
    property: 'sourceDate',
    heading: 'Data Last\nRefreshed Date',
    getValue: (a) => a.refreshDate || '',
  },
  refreshDate: {
    property: 'refreshDate',
    heading: 'Data Source Date',
    getValue: (a) => (a as AssetLayer).sourceDate || '',
  },
  mapRequested: {
    property: 'mapRequested',
    heading: 'Map Requested',
    getValue: (a) => (isValidStatus(a as RequestableLayer) ? 'X' : ''),
  },
  mapRequestCreatedAt: {
    property: 'createdAt',
    heading: 'Map Request\nInitiated Date',
    getValue: (a) => (a as RequestableLayer).createdAt || '',
  },
  mapRequestStatus: {
    property: 'requestStatus',
    heading: 'Map Request Status',
    getValue: (a) => {
      const s = (a as RequestableLayer).status;
      if (isValidStatus(a as RequestableLayer)) {
        return requestStatusMap[s] || '';
      }

      return '';
    },
  },
  mapRequestCompletedDate: {
    property: 'resolutionDate',
    heading: 'Map Request\nCompleted Date',
    getValue: (a) => (a as RequestableLayer).resolutionDate || '',
  },
  utilitiesPresent: {
    property: 'utilitiesPresent',
    heading: 'Map Request\nOutcome/Determination',
    getValue: (a) => {
      const p = (a as RequestableLayer).utilitiesPresent;

      if (p) {
        return assetsPresentMap[p] || '';
      }

      return '';
    },
  },
  notes: {
    property: 'notes',
    heading: 'Map Request\nNotes',
    getValue: (a) => (a as RequestableLayer).notes || '',
  },
  firstEmailed: {
    property: 'firstEmailed',
    heading: 'Map Request\n1st Email\nContact Date',
    getValue: (a) => (a as RequestableLayer).firstEmailed || '',
  },
  secondEmailed: {
    property: 'secondEmailed',
    heading: 'Map Request\n2nd Email\nContact Date',
    getValue: (a) => (a as RequestableLayer).secondEmailed || '',
  },
  thirdEmailed: {
    property: 'thirdEmailed',
    heading: 'Map Request\n3rd Email\nContact Date',
    getValue: (a) => (a as RequestableLayer).thirdEmailed || '',
  },
  fourthEmailed: {
    property: 'fourthEmailed',
    heading: 'Map Request\n4th Email\nContact Date',
    getValue: (a) => (a as RequestableLayer).thirdEmailed || '',
  },
};

export const asbuiltConfig: { [key: string]: Config } = {
  asbuiltRequested: {
    property: 'asbuiltRequested',
    heading: 'As-Built Requested',
    getValue: config.mapRequested.getValue,
  },
  asbuiltRequestedCreatedAt: {
    property: 'asbuiltRequestedCreatedAt',
    heading: 'As-Built\nRequested\nInitiated Date',
    getValue: config.mapRequestCreatedAt.getValue,
  },
  asbuiltRequestStatus: {
    property: 'asbuiltRequestStatus',
    heading: 'As-Built\nRequest Status',
    getValue: config.mapRequestStatus.getValue,
  },
  asbuiltRequestCompletedDate: {
    property: 'asbuiltResolutionDate',
    heading: 'As-Built Request\nCompleted Date',
    getValue: config.mapRequestCompletedDate.getValue,
  },
  asbuiltUtilitiesPresent: {
    property: 'asbuiltUtilitiesPresent',
    heading: 'As-Built Request\nOutcome/Determination',
    getValue: config.utilitiesPresent.getValue,
  },
  asbuiltNotes: {
    property: 'asbuiltNotes',
    heading: 'As-Built Request\nNotes',
    getValue: config.notes.getValue,
  },
  asbuiltFirstEmailed: {
    property: 'asbuiltFirstEmailed',
    heading: 'As-Built Request\n1st Email\nContact Date',
    getValue: config.firstEmailed.getValue,
  },
  asbuiltSecondEmailed: {
    property: 'asbuiltSecondEmailed',
    heading: 'As-Built Request\n2nd Email\nContact Date',
    getValue: config.secondEmailed.getValue,
  },
  asbuiltThirdEmailed: {
    property: 'asbuiltThirdEmailed',
    heading: 'As-Built Request\n3rd Email\nContact Date',
    getValue: config.thirdEmailed.getValue,
  },
  asbuiltFourthEmailed: {
    property: 'asbuiltFourthEmailed',
    heading: 'As-Built Request\n4th Email\nContact Date',
    getValue: config.fourthEmailed.getValue,
  },
};

export interface CSVExportProps {
  [key: string]: any;
}

export const defaultHeadings: string[] = [
  'Agency Name',
  'Type',
  'Currently Available on CivilGrid',
  'Map Request on CivilGrid',
  'As-Built Request on CivilGrid',
];

export const generateCSV = (rows: CSVExportProps[]) => {
  const headings = [
    ...defaultHeadings,
    ...Object.values(config).map((item) => removeRegEx(item.heading)),
    ...Object.values(asbuiltConfig).map((item) => removeRegEx(item.heading)),
  ];

  let csvData = `${headings.join(', ')}\n`;

  rows.forEach((row) => {
    csvData += `${Object.values(row).join(', ')}\n`;
  });

  return csvData;
};

export const downloadCSV = (csv: string, name: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const fileName = `Outreach Log - ${name} - ${new Date().toLocaleDateString()}`;

  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', fileName);
  link.click();
};

export const removeRegEx = (value: string) => {
  return value.replaceAll('\n', ' ').replaceAll('\r', ' ');
};
